import React, { useEffect, useRef } from 'react';
import { Field, Modal } from '../../components/ui';
import { fmtDate, fullName, VISIBILITIES } from '../../utils/helpers';
import { useFamilyMemberForm } from './useFamilyMemberForm';

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayOf = (d) => { const t = new Date(`${String(d || '').slice(0, 10)}T00:00:00`); return Number.isNaN(t.getTime()) ? '' : DAY[t.getDay()]; };

// Desktop "add member to family": a compact 600px dialog - New / Existing as
// one switch, Name | Phone | Visibility on one line, the family's functions as
// two columns of bordered rows. First box takes the keyboard; Enter in the
// phone box (or any box without suggestions) adds the member.
export default function FamilyMemberFormDesktop(props) {
  const { head, onClose, functions = [] } = props;   // the member inherits the family head's side
  const { mode, setMode, form, setF, existingGuestId, setExistingGuestId, available, saving, canSubmit, submit,
    canImport, importContact, knownNames, familyFunctions, fnIds, toggleFunction } = useFamilyMemberForm(props);

  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.querySelector('input:not([type="checkbox"]), select')?.focus(); }, [mode]);
  const dateOf = (id) => (functions.find(f => String(f.FunctionId) === String(id)) || {}).FunctionDate;
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT' || e.target.type === 'checkbox') return;
    if (e.target.getAttribute('list')) return; // Enter there picks a suggestion
    e.preventDefault();
    if (canSubmit && !saving) submit();
  };

  return (
    <Modal
      className="gx-fm-modal"
      title={`Add Member to ${fullName(head)}'s Family`}
      onClose={onClose}
      footer={<>
        <span className="cg-summary">
          {mode === 'existing' ? `${available.length} individual guest${available.length === 1 ? '' : 's'} to pick from`
            : familyFunctions.length > 1 ? <><strong>{fnIds.length}</strong> of {familyFunctions.length} functions</> : 'Surname comes from the head'}
        </span>
        <button className="btn light" type="button" onClick={onClose}>Cancel</button>
        <button className="btn primary" type="button" disabled={saving || !canSubmit} onClick={submit}>
          {saving ? 'Saving...' : 'Add to Family'}
        </button>
      </>}
    >
      <div ref={bodyRef} onKeyDown={onKeyDown}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <div className="gx-seg">
            <button type="button" className={mode === 'new' ? 'on' : ''} onClick={() => setMode('new')}>New Guest</button>
            <button type="button" className={mode === 'existing' ? 'on' : ''}
              disabled={!available.length} onClick={() => setMode('existing')}>Pick Existing Guest</button>
          </div>
          {mode !== 'existing' && canImport && (
            <button type="button" className="btn light sm" onClick={importContact}>📇 Phone book</button>
          )}
        </div>

        {mode === 'existing' ? (
          <Field label="Existing individual guest" type="select" value={existingGuestId} required
            options={available.map(row => ({ value: row.GuestId, label: `${fullName(row)} · ${row.MobileNumber || 'No phone'}` }))}
            onChange={setExistingGuestId} />
        ) : (
          <>
            <div className="muted gx-fm-note">
              If this phone number already belongs to an individual guest, that guest will automatically move into this family.
            </div>
            {/* The surname comes from the family head; a number is optional. */}
            <div className="gx-fm-row">
              <Field label="Name" value={form.GuestName} required suggest={knownNames} onChange={v => setF('GuestName', v)} />
              <Field label="Phone Number" placeholder="optional" value={form.MobileNumber} onChange={v => setF('MobileNumber', v)} />
              <Field label="Visibility" type="select" value={form.Visibility} options={VISIBILITIES} onChange={v => setF('Visibility', v)} />
            </div>
            {/* Invited to: starts as the whole family's list; untick what this
                person does not attend. */}
            {familyFunctions.length > 1 && (
              <>
                <div className="gx-fn-head"><span className="step-label">Invited to</span></div>
                <div className="gx-fn-grid two" style={{ marginBottom: 10 }}>
                  {familyFunctions.map(f => {
                    const on = fnIds.includes(String(f.FunctionId));
                    const d = dateOf(f.FunctionId);
                    return (
                      <label key={f.FunctionId} className={'cg-fn' + (on ? ' on' : '')}>
                        <input type="checkbox" checked={on} onChange={() => toggleFunction(f.FunctionId)} />
                        <span className="cg-fn-name" title={f.FunctionName}>{f.FunctionName}</span>
                        {d && <span className="cg-fn-date">{dayOf(d)} {fmtDate(d).slice(0, 5)}</span>}
                      </label>
                    );
                  })}
                </div>
              </>
            )}
            <div className="gx-fm-remarks">
              <Field label="Remarks" type="textarea" rows={2} value={form.Remarks} onChange={v => setF('Remarks', v)} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
